import styled from "@emotion/styled";
import { Box, FormControlLabel, Grid, Radio, RadioGroup, Stack, Typography, type TypographyProps } from "@mui/material";
import { StyledFormControl, StyledFormInput, StyledFormLabelWrapper, StyledValidationText } from "Forms/Components/FormStyles";
import { useFieldError } from "Forms/Hooks/useFieldError";
import type { CodeListItem } from "Infrastructure/Models/ICodeListDto";
import { Spacing } from "Infrastructure/Themes/Themes";
import { get } from "lodash-es";
import { type Control, Controller, type FieldError, type FieldErrors, type FieldPath, type FieldValues } from "react-hook-form";

type Props<FormType extends FieldValues> = {
  codeList: CodeListItem[];
  name: FieldPath<FormType>;
  control: Control<FormType, object>;
  label?: string;
  useCodesAsLabels?: boolean;
  disabled?: boolean;
  errors: FieldErrors<FormType>;
  row?: boolean;
  gridSize?: number;
  slotProps?: {
    typography?: TypographyProps;
  };
  onChange?: (value: string) => void;
};

const StyledFormControlLabel = styled(FormControlLabel)`
  display: flex;
  flex-flow: row;
  align-items: center;
  margin-right: 0;
`;

const StyledIcon = styled.div<{ $isDisabled: boolean }>`
  width: 16px;
  min-width: 16px;
  height: 16px;
  position: relative;
  border-radius: 50%;
  border: 1px solid
    ${(props) => (props.$isDisabled ? props.theme.colors.radioBtnDisabled : props.theme.colors.border)};
`;

const StyledIconChecked = styled(StyledIcon)<{ $isDisabled: boolean }>`
  border: 1px solid;
  background: ${(props) => (props.$isDisabled ? props.theme.colors.radioBtnDisabled : props.theme.palette.primary.main)};
      
  .bl-radio-buttons__label &::before {
      content: "";
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      width: 6px;
      height: 6px;
      background-color: ${(props) => props.theme.colors.white};
      border-radius: 50%;
    }
`;

const Description = styled(Typography)`
  color: ${(props) => props.theme.palette.text.secondary};
  font-weight: 400;
`;

export const BlRadioButtons = <T extends object>(props: Props<T>) => {
  const { codeList, name, control, useCodesAsLabels, label, disabled, errors, row, slotProps, gridSize, onChange: propsOnChange } = props;
  const defaultValue = codeList[0]?.code;
  const { hasError, errorMessage } = useFieldError({
    fieldError: get(errors, name) as FieldError,
  });

  return (
    <StyledFormControl>
      {!!label && (
        <StyledFormLabelWrapper htmlFor={name} $disabled={disabled ?? false}>
          <Stack direction="row" gap={1}>
            <Typography fontWeight={500} variant="body2" mb={Spacing.S}>
              {label}
            </Typography>
          </Stack>
        </StyledFormLabelWrapper>
      )}
      <StyledFormInput>
        <Controller
          control={control}
          defaultValue={defaultValue as any}
          name={name}
          render={({ field: { value, name, onChange, onBlur } }) => (
            <RadioGroup
              sx={{ ml: Spacing.S }}
              value={value ?? ""}
              name={name}
              onChange={(e) => {
                const value = e.target.value;
                propsOnChange?.(value);
                onChange(value);
              }}
              onBlur={onBlur}
              row={row ?? true}
            >
              <Grid container columnSpacing={Spacing.XL} width="100%">
                {codeList.map((item) => (
                  <Grid size={gridSize ?? 6} key={item.code.toString()}>
                    <StyledFormControlLabel
                      className="bl-radio-buttons__label"
                      disabled={disabled}
                      value={item.code.toString()}
                      control={
                        <Radio
                          icon={<StyledIcon $isDisabled={disabled ?? false} />}
                          checkedIcon={<StyledIconChecked $isDisabled={disabled ?? false} />}
                        />
                      }
                      slotProps={slotProps}
                      label={
                        <Box className="bl-radio-buttons__label-content">
                          <Stack direction="row">
                            <Typography variant="body2" fontWeight={500}>
                              {useCodesAsLabels === true ? item.code : item.name}
                            </Typography>
                            <Box ml={0.5}>{item.endAdornment}</Box>
                          </Stack>

                          {!!item.description &&
                            (typeof item.description === "string" ? (
                              <Description>{item.description}</Description>
                            ) : (
                              <Box>{item.description}</Box>
                            ))}
                        </Box>
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            </RadioGroup>
          )}
        />
      </StyledFormInput>
      {hasError && <StyledValidationText>{errorMessage}</StyledValidationText>}
    </StyledFormControl>
  );
};
